/**
 * @blockcast/transport - Adaptive Bitrate Controller
 *
 * Progressive adaptive bitrate increase based on network conditions.
 * Monitors received bandwidth and packet loss to adjust quality levels.
 *
 * When FEC is enabled, ABR decisions use the **effective loss rate** after FEC
 * recovery, not the raw transport-level loss rate, so FEC can mask burst losses
 * without unnecessarily downgrading quality. This is libmmt policy; no draft
 * specifies it.
 */

import type { QualityLevel } from "./types.js";
import { selectMulticastTier, type MulticastTierEndpoint } from "./multicast-tier.js";

/**
 * FEC statistics for ABR decisions
 */
export interface FecStatsForAbr {
	/** Raw loss rate before FEC recovery (0.0-1.0) */
	rawLossRate: number;
	/** Effective loss rate after FEC recovery (0.0-1.0) */
	effectiveLossRate: number;
	/** FEC recovery rate (percentage of lost packets recovered) */
	recoveryRate: number;
	/** Whether FEC is enabled */
	fecEnabled: boolean;
}

/**
 * A generic 240p..4K ladder, kept as public API only.
 *
 * @deprecated Not a stream's ladder and never a default: the controller
 * adapts over the ladder its caller supplies (`AdaptiveBitrateControllerOptions.ladder`,
 * e.g. the catalog's video renditions). Walking this list is how a player on a
 * 360p/480p/720p catalog logged upgrades to 1080p and 1440p it could never
 * switch to (BLO-35597).
 */
export const QUALITY_LEVELS: readonly QualityLevel[] = Object.freeze([
	{ name: "240p", bitrate: 300_000, width: 426, height: 240, fps: 15 },
	{ name: "360p", bitrate: 500_000, width: 640, height: 360, fps: 24 },
	{ name: "480p", bitrate: 1_000_000, width: 854, height: 480, fps: 30 },
	{ name: "720p", bitrate: 2_500_000, width: 1280, height: 720, fps: 30 },
	{ name: "1080p", bitrate: 5_000_000, width: 1920, height: 1080, fps: 30 },
	{ name: "1440p", bitrate: 8_000_000, width: 2560, height: 1440, fps: 30 },
	{ name: "4K", bitrate: 15_000_000, width: 3840, height: 2160, fps: 30 },
].map((level) => Object.freeze(level)));

/** What the controller adapts over and where it starts; both from the caller. */
export interface AdaptiveBitrateControllerOptions {
	/**
	 * The renditions this stream carries (e.g. the catalog's video tracks), in
	 * any order: a unique name and a bitrate (bps) each, plus dimensions and
	 * fps when the catalog states them. Renditions may share a bitrate.
	 */
	ladder: readonly QualityLevel[];
	/** Name of the ladder rendition presented at construction; the lowest
	 *  rung when omitted. A name that matches no rung is refused. */
	initialQuality?: string;
}

function positiveFinite(value: unknown): value is number {
	return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * Validate a caller ladder and return it frozen, ascending by bitrate. Throws
 * on an empty ladder, a rung without a name or a positive finite bitrate, a
 * width, height or fps that is present but not positive finite, or a name that
 * appears twice: a name is how `setQuality` and the initial rendition find a
 * rung. A shared bitrate is legal (e.g. 720p30 and 720p60, or two codecs);
 * `Array.prototype.sort` is stable, so equal bitrates keep the caller's order.
 */
export function validateQualityLadder(ladder: readonly QualityLevel[]): readonly QualityLevel[] {
	if (!Array.isArray(ladder) || ladder.length === 0) {
		throw new TypeError("[ABR] ladder must list at least one rendition (e.g. the catalog's video tracks)");
	}
	const names = new Set<string>();
	for (const level of ladder) {
		if (typeof level?.name !== "string" || level.name.length === 0) {
			throw new TypeError("[ABR] every ladder rendition needs a name");
		}
		if (!positiveFinite(level.bitrate)) {
			throw new RangeError(`[ABR] rendition ${level.name}: bitrate must be a positive finite number, got ${String(level.bitrate)}`);
		}
		for (const field of ["width", "height", "fps"] as const) {
			if (level[field] !== undefined && !positiveFinite(level[field])) {
				throw new RangeError(`[ABR] rendition ${level.name}: ${field} must be a positive finite number when stated, got ${String(level[field])}`);
			}
		}
		if (names.has(level.name)) throw new RangeError(`[ABR] rendition ${level.name} appears twice in the ladder`);
		names.add(level.name);
	}
	return Object.freeze(
		[...ladder].sort((a, b) => a.bitrate - b.bitrate).map((level) => Object.freeze({ ...level })),
	);
}

/**
 * Progressive Adaptive Bitrate Controller
 *
 * Features:
 * - Monitors bandwidth via received bytes over time window
 * - Tracks packet loss rate (raw and effective after FEC)
 * - Uses effective loss rate (after FEC) for ABR decisions
 * - Progressive quality upgrades (waits for stability)
 * - Fast quality downgrades on congestion
 */
export class AdaptiveBitrateController {
	/** The caller's renditions, ascending by bitrate (`validateQualityLadder`). */
	readonly ladder: readonly QualityLevel[];
	private currentQualityIndex: number;
	private history: { timestamp: number; bytesReceived: number; packetsLost: number }[] = [];
	private readonly historyWindow = 10_000; // 10 second window
	private readonly upgradeThreshold = 0.8; // 80% of target bitrate sustained
	private readonly downgradeThreshold = 0.5; // 50% of target bitrate triggers downgrade
	private readonly stabilityPeriod = 5_000; // Wait 5s before upgrading

	private lastQualityChange = 0;
	private onQualityChange?: (quality: QualityLevel) => void;

	// FEC stats for effective loss rate calculation
	private fecStats: FecStatsForAbr = {
		rawLossRate: 0,
		effectiveLossRate: 0,
		recoveryRate: 1,
		fecEnabled: false,
	};

	constructor(options: AdaptiveBitrateControllerOptions) {
		if (!options || typeof options !== "object") {
			throw new TypeError("[ABR] AdaptiveBitrateController needs { ladder, initialQuality }");
		}
		this.ladder = validateQualityLadder(options.ladder);
		this.currentQualityIndex =
			options.initialQuality === undefined ? 0 : this.ladder.findIndex((q) => q.name === options.initialQuality);
		if (this.currentQualityIndex < 0) {
			throw new RangeError(`[ABR] initial rendition ${String(options.initialQuality)} is not in the ladder`);
		}
	}

	setCallback(callback: (quality: QualityLevel) => void) {
		this.onQualityChange = callback;
	}

	getCurrentQuality(): QualityLevel {
		return this.ladder[this.currentQualityIndex];
	}

	/** Selects the multicast (S,G) tier without replacing a player's unicast ABR manager. */
	selectMulticastTier(
		endpoints: readonly MulticastTierEndpoint[],
		estimate: number | undefined = this.getMetrics().measuredBitrate || undefined,
	): MulticastTierEndpoint | undefined {
		return selectMulticastTier(endpoints, estimate);
	}

	/**
	 * Record network sample for bitrate adaptation
	 *
	 * @param bytesReceived - Bytes received in this sample
	 * @param packetsLost - Packets lost (raw, before FEC recovery)
	 */
	recordSample(bytesReceived: number, packetsLost: number) {
		const now = Date.now();

		this.history.push({ timestamp: now, bytesReceived, packetsLost });

		// Prune old samples
		this.history = this.history.filter((s) => now - s.timestamp < this.historyWindow);

		// Calculate metrics
		const { measuredBitrate, lossRate: rawLossRate } = this.calculateMetrics();
		const currentQuality = this.getCurrentQuality();

		// Use effective loss rate after FEC for ABR decisions
		// This allows FEC to mask burst losses without triggering unnecessary downgrades
		const effectiveLossRate = this.fecStats.fecEnabled
			? this.fecStats.effectiveLossRate
			: rawLossRate;

		// Decision logic
		if (now - this.lastQualityChange < this.stabilityPeriod) {
			return; // Wait for stability
		}

		// Check for downgrade - use effective loss rate (post-FEC)
		// Only downgrade if FEC cannot recover the losses
		if (effectiveLossRate > 0.05 || measuredBitrate < currentQuality.bitrate * this.downgradeThreshold) {
			this.downgrade();
			return;
		}

		// Check for upgrade (progressive increase) - use effective loss rate
		if (
			measuredBitrate > currentQuality.bitrate * this.upgradeThreshold &&
			effectiveLossRate < 0.01 &&
			this.currentQualityIndex < this.ladder.length - 1
		) {
			this.upgrade();
		}
	}

	/**
	 * Update FEC statistics for ABR decisions
	 *
	 * ABR uses the effective loss rate (after FEC recovery) rather than the
	 * raw transport loss rate.
	 *
	 * @param stats - FEC statistics from the FEC decoder
	 */
	setFecStats(stats: FecStatsForAbr) {
		this.fecStats = stats;
	}

	/**
	 * Get current FEC statistics
	 */
	getFecStats(): FecStatsForAbr {
		return { ...this.fecStats };
	}

	private calculateMetrics() {
		if (this.history.length < 2) {
			return { measuredBitrate: 0, lossRate: 0 };
		}

		const totalBytes = this.history.reduce((sum, s) => sum + s.bytesReceived, 0);
		const totalLost = this.history.reduce((sum, s) => sum + s.packetsLost, 0);
		const duration = (this.history[this.history.length - 1].timestamp - this.history[0].timestamp) / 1000;

		const measuredBitrate = duration > 0 ? (totalBytes * 8) / duration : 0;
		const lossRate = totalLost / (this.history.length + totalLost);

		return { measuredBitrate, lossRate };
	}

	private upgrade() {
		if (this.currentQualityIndex >= this.ladder.length - 1) return;

		this.currentQualityIndex++;
		this.lastQualityChange = Date.now();

		const newQuality = this.getCurrentQuality();
		console.log(`[ABR] Upgrading to ${newQuality.name} (${newQuality.bitrate / 1_000_000}Mbps)`);

		this.onQualityChange?.(newQuality);
	}

	private downgrade() {
		if (this.currentQualityIndex <= 0) return;

		this.currentQualityIndex--;
		this.lastQualityChange = Date.now();

		const newQuality = this.getCurrentQuality();
		console.log(`[ABR] Downgrading to ${newQuality.name} (${newQuality.bitrate / 1_000_000}Mbps)`);

		this.onQualityChange?.(newQuality);
	}

	/**
	 * Select a ladder rendition: the caller switched to it (a manual override,
	 * or a switch the caller made). Throws for a name the ladder does not carry.
	 */
	setQuality(name: string) {
		const index = this.ladder.findIndex((q) => q.name === name);
		if (index < 0) throw new RangeError(`[ABR] rendition ${name} is not in the ladder`);
		this.currentQualityIndex = index;
		this.lastQualityChange = Date.now();
		this.onQualityChange?.(this.getCurrentQuality());
	}

	/**
	 * Get current metrics including both raw and effective loss rates
	 */
	getMetrics(): {
		measuredBitrate: number;
		rawLossRate: number;
		effectiveLossRate: number;
		fecEnabled: boolean;
		sampleCount: number;
	} {
		const { measuredBitrate, lossRate: rawLossRate } = this.calculateMetrics();
		return {
			measuredBitrate,
			rawLossRate,
			effectiveLossRate: this.fecStats.fecEnabled ? this.fecStats.effectiveLossRate : rawLossRate,
			fecEnabled: this.fecStats.fecEnabled,
			sampleCount: this.history.length,
		};
	}

	/**
	 * Reset history and start fresh
	 */
	reset() {
		this.history = [];
		this.lastQualityChange = 0;
	}
}
